import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { theme } from './styles/theme';
import { GlobalStyles } from './styles/GlobalStyles';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Components
import UserTypeSelection from './components/auth/UserTypeSelection';
import AuthFlow from './components/auth/AuthFlow';
import AzureCallback from './components/auth/AzureCallback';
import TestRegister from './components/auth/TestRegister';
import TestConnection from './components/rutas/TestConnection';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Dashboard from './components/dashboard/Dashboard';
import RutasModule from './components/rutas/RutasModule';
import RutasGranelModule from './components/rutas/RutasGranelModule';
import LicitacionesModule from './components/licitaciones/LicitacionesModule';
import PreguntasModule from './components/preguntas/PreguntasModule';
import BaseModule from './components/base/BaseModule';
import ProveedoresModule from './components/proveedores/ProveedoresModule';
import Layout from './components/layout/Layout';

// Componente para redirigir a la página inicial según el tipo de usuario
const HomeRedirect: React.FC = () => {
  const { proveedor } = useAuth();
  const isAdmin = proveedor?.username === 'ADMINISTRADOR';
  
  // Si es administrador, redirigir al dashboard
  if (isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  // Si es proponente, redirigir a rutas
  return <Navigate to="/rutas" replace />;
};

// Componente para proteger rutas solo para Proponentes
const ProponenteRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { proveedor } = useAuth();
  const isAdmin = proveedor?.username === 'ADMINISTRADOR';
  
  // Si es administrador, redirigir al dashboard
  if (isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  // Si es proponente, permitir acceso
  return <>{children}</>;
};

// Componente para proteger rutas solo para Administradores
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { proveedor } = useAuth();
  const isAdmin = proveedor?.username === 'ADMINISTRADOR';
  
  // Si NO es administrador, redirigir a rutas
  if (!isAdmin) {
    return <Navigate to="/rutas" replace />;
  }
  
  // Si es administrador, permitir acceso
  return <>{children}</>;
};

const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const [userType, setUserType] = useState<'externo' | 'interno' | null>(null);

  // Verificar si estamos en el callback de Azure (hay un código en la URL)
  const urlParams = new URLSearchParams(window.location.search);
  const hasAuthCode = urlParams.has('code');

  if (isLoading) {
    return null; // El ProtectedRoute mostrará el spinner de carga
  }

  // Si hay un código de autenticación, mostrar el componente de callback
  if (hasAuthCode && !isAuthenticated) {
    return <AzureCallback />;
  }

  if (!isAuthenticated) {
    // Primero mostrar selección de tipo de usuario
    if (!userType) {
      return <UserTypeSelection onSelectType={setUserType} />;
    }
    // Luego mostrar el flujo de autenticación según el tipo
    return <AuthFlow userType={userType} onBack={() => setUserType(null)} />;
  }

  return (
    <Routes>
      <Route path="/" element={<Layout />}>
        <Route index element={<HomeRedirect />} />
        <Route path="dashboard" element={
          <ProtectedRoute>
            <AdminRoute>
              <Dashboard />
            </AdminRoute>
          </ProtectedRoute>
        } />
        <Route path="licitaciones" element={
          <ProtectedRoute>
            <AdminRoute>
              <LicitacionesModule />
            </AdminRoute>
          </ProtectedRoute>
        } />
        <Route path="preguntas" element={
          <ProtectedRoute>
            <PreguntasModule />
          </ProtectedRoute>
        } />
        <Route path="rutas" element={
          <ProtectedRoute>
            <ProponenteRoute>
              <RutasModule />
            </ProponenteRoute>
          </ProtectedRoute>
        } />
        <Route path="rutas-granel" element={
          <ProtectedRoute>
            <ProponenteRoute>
              <RutasGranelModule />
            </ProponenteRoute>
          </ProtectedRoute>
        } />
        <Route path="base" element={
          <ProtectedRoute>
            <AdminRoute>
              <BaseModule />
            </AdminRoute>
          </ProtectedRoute>
        } />
        <Route path="proveedores" element={
          <ProtectedRoute>
            <AdminRoute>
              <ProveedoresModule />
            </AdminRoute>
          </ProtectedRoute>
        } />
      </Route>
      
      {/* Ruta de prueba para debug */}
      <Route path="/test-register" element={<TestRegister />} />
      <Route path="/test-connection" element={<TestConnection />} />
      
      {/* Ruta por defecto */}
      <Route path="*" element={<HomeRedirect />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles />
      <Router>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;